import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { ProximamenteModal } from "./ProximamenteModal";
import { api } from "../lib/api";
import { useAuth } from "../hooks/useAuth";

function Check({ color }: { color: string }) {
  return <span style={{ color, fontWeight: 700, flexShrink: 0 }}>✓</span>;
}

const PLAN_LIBRE = [
  "1 sesión psicológica gratuita al registrarte",
  "Acceso completo al foro comunitario",
  "Guías psicoeducativas básicas",
  "Material de apoyo limitado",
  "Un acceso al mes a sesión comunitaria",
  "Perfil personal",
];

const PLAN_PLUS = [
  "Todo lo del plan Libre",
  "Biblioteca completa sin límites",
  "Todo el material de apoyo y ejercicios",
  "2 sesiones psicológicas de 40 min/mes",
  "1 sesión al mes con partners de la comunidad (aplica restricciones)",
];

const PLAN_PRO = [
  "Todo lo del plan Plus",
  "3 citas psicológicas de 50 min/mes",
  "Seguimiento profesional personalizado",
  "Historial clínico digital",
  "Acceso prioritario a nuevos recursos",
  "Sesiones preferenciales con partners",
];

const PLAN_MAX = [
  "Todo lo del plan Pro",
  "5 citas psicológicas de 60 min/mes",
  "Sesiones de grupo con especialistas invitados",
  "Plan de desarrollo psicológico personalizado",
  "Certificado de proceso terapéutico (3 meses)",
  "Acceso a talleres psicológicos",
  "Canal directo con tu psicólogo",
];

type PlanTier = "libre" | "plus" | "pro" | "max";
const TIER_RANK: Record<PlanTier, number> = { libre: 0, plus: 1, pro: 2, max: 3 };

export function Membresia() {
  const [anual, setAnual] = useState(false);
  const { token, isAuthenticated } = useAuth();
  const [currentTier, setCurrentTier] = useState<PlanTier>("libre");
  const [proximamentePlan, setProximamentePlan] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && token) {
      api.getSubscripcion(token).then((r) => {
        if (r.ok) setCurrentTier(r.data.plan_tier);
      });
    }
  }, [isAuthenticated, token]);

  function renderPlanButton(plan: Exclude<PlanTier, "libre">, label: string, style: CSSProperties) {
    if (isAuthenticated && currentTier === plan) {
      return (
        <button type="button" className="btn btn--full" disabled style={{ ...style, opacity: 0.6, cursor: "default" }}>
          Plan actual
        </button>
      );
    }
    const esMejora = isAuthenticated && TIER_RANK[plan] > TIER_RANK[currentTier];
    const texto = !isAuthenticated ? "Comenzar" : esMejora ? `Mejorar a ${label}` : `Cambiar a ${label}`;
    return (
      <button type="button" className="btn btn--full" style={style} onClick={() => setProximamentePlan(label)}>
        {texto}
      </button>
    );
  }

  return (
    <div id="membresia" className="container" style={{ padding: "78px 0 30px" }}>
      <Reveal>
        <div style={{ textAlign: "center", marginBottom: 30 }}>
          <h2 className="section-title-display fluid-heading-md" style={{ marginBottom: 10 }}>
            Elige cómo quieres empezar
          </h2>
          <p style={{ fontSize: 17, color: "#8A8A9A", margin: "0 0 28px" }}>
            Sin presión. A tu ritmo. Con el respaldo que necesitas.
          </p>
          <div className="toggle-pill">
            <button
              type="button"
              className={`toggle-pill__option${!anual ? " toggle-pill__option--active" : ""}`}
              onClick={() => setAnual(false)}
            >
              Mensual
            </button>
            <button
              type="button"
              className={`toggle-pill__option${anual ? " toggle-pill__option--active" : ""}`}
              onClick={() => setAnual(true)}
            >
              Anual — 2 meses gratis
            </button>
          </div>
        </div>
      </Reveal>

      <div className="pricing-grid-v2">
        {/* PLAN LIBRE */}
        <Reveal style={{ transitionDelay: "0ms" }}>
          <div className="pricing-card-v2" style={{ background: "#FAFAF8", border: "1px solid #ECE3D8" }}>
            <span className="badge" style={{ background: "#B8D4C8", color: "#2F5742", marginBottom: 18 }}>
              Para empezar
            </span>
            <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 17, color: "#2D2D2D", margin: 0 }}>
              Gratis
            </h3>
            <div className="pricing-price pricing-price-row" style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 38, color: "#2D2D2D", margin: "8px 0 14px" }}>
              Gratis
            </div>
            <p style={{ fontSize: 14, color: "#6B6258", lineHeight: 1.6, margin: 0 }}>
              Da el primer paso sin compromisos. Accede a nuestra comunidad y comienza a conectar con personas que entienden lo que vives.
            </p>
            <ul className="pricing-check-list">
              {PLAN_LIBRE.map((f) => (
                <li key={f}><Check color="#6FA98A" />{f}</li>
              ))}
            </ul>
            {isAuthenticated && currentTier === "libre" ? (
              <button type="button" className="btn btn-outline btn--full" disabled style={{ opacity: 0.6, cursor: "default" }}>
                Plan actual
              </button>
            ) : (
              <Link to="/registro" className="btn btn-outline btn--full">Comenzar gratis</Link>
            )}
            <p style={{ fontSize: 11.5, color: "#9a9088", lineHeight: 1.4, margin: "10px 0 0" }}>
              * La sesión gratuita se agenda tras completar tu registro
            </p>
          </div>
        </Reveal>

        {/* PLAN PLUS */}
        <Reveal style={{ transitionDelay: "100ms" }}>
          <div
            className="pricing-card-v2 pricing-card-v2--plus"
            style={{ background: "#fff", border: "2px solid #C49A8A", boxShadow: "0 24px 50px -24px rgba(196,154,138,0.55)" }}
          >
            <span className="badge" style={{ background: "#C49A8A", color: "#fff", marginBottom: 18 }}>
              Más popular
            </span>
            <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 17, color: "#2D2D2D", margin: 0 }}>
              Plus
            </h3>
            <div className="pricing-price-row" style={{ display: "flex", alignItems: "baseline", gap: 6, margin: "8px 0 14px" }}>
              <span className="pricing-price" style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 38, color: "#2D2D2D" }}>
                $300
              </span>
              <span style={{ fontSize: 14, color: "#8A8A9A" }}>/mes</span>
              <span style={{ fontSize: 12, color: "#8A8A9A" }}>MXN</span>
            </div>
            <p style={{ fontSize: 14, color: "#6B6258", lineHeight: 1.6, margin: 0 }}>
              Para quienes están listos para ir más profundo. Acompañamiento profesional real con sesiones mensuales y acceso completo al material.
            </p>
            <ul className="pricing-check-list">
              {PLAN_PLUS.map((f) => (
                <li key={f}><Check color="#C49A8A" />{f}</li>
              ))}
            </ul>
            {renderPlanButton("plus", "Plus", { background: "#C49A8A", color: "#fff", boxShadow: "0 14px 26px -12px rgba(196,154,138,0.7)" })}
          </div>
        </Reveal>

        {/* PLAN PRO */}
        <Reveal style={{ transitionDelay: "200ms" }}>
          <div
            className="pricing-card-v2"
            style={{ background: "linear-gradient(160deg, #2D2D2D, #3D3D4D)", color: "#fff" }}
          >
            <span className="badge" style={{ background: "#2D2D2D", color: "#fff", border: "1px solid rgba(255,255,255,.2)", marginBottom: 18 }}>
              Transformación completa
            </span>
            <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 17, color: "#fff", margin: 0 }}>
              Pro
            </h3>
            <div className="pricing-price-row" style={{ display: "flex", alignItems: "baseline", gap: 6, margin: "8px 0 14px" }}>
              <span className="pricing-price" style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 38, color: "#fff" }}>
                $700
              </span>
              <span style={{ fontSize: 14, color: "#C49A8A" }}>/mes</span>
              <span style={{ fontSize: 12, color: "#C49A8A" }}>MXN</span>
            </div>
            <p style={{ fontSize: 14, color: "rgba(255,255,255,.75)", lineHeight: 1.6, margin: 0 }}>
              Para quienes buscan un proceso serio de transformación con seguimiento profesional constante y el máximo acompañamiento disponible.
            </p>
            <ul className="pricing-check-list" style={{ color: "rgba(255,255,255,.9)" }}>
              {PLAN_PRO.map((f) => (
                <li key={f}><Check color="#C49A8A" />{f}</li>
              ))}
            </ul>
            {renderPlanButton("pro", "Pro", { background: "#fff", color: "#2D2D2D" })}
          </div>
        </Reveal>

        {/* PLAN MAX */}
        <Reveal style={{ transitionDelay: "300ms" }}>
          <div className="pricing-card-v2 pricing-card-v2--gold" style={{ color: "#fff" }}>
            <span className="badge" style={{ background: "rgba(212,180,100,0.15)", color: "#D4B464", border: "1px solid rgba(212,180,100,0.4)", marginBottom: 18 }}>
              ✦ Exclusivo
            </span>
            <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 17, color: "#D4B464", margin: 0 }}>
              Max
            </h3>
            <div className="pricing-price-row" style={{ display: "flex", alignItems: "baseline", gap: 6, margin: "8px 0 14px" }}>
              <span className="pricing-price" style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 38, color: "#D4B464" }}>
                $1,500
              </span>
              <span style={{ fontSize: 14, color: "rgba(212,180,100,0.7)" }}>/mes</span>
              <span style={{ fontSize: 12, color: "rgba(212,180,100,0.7)" }}>MXN</span>
            </div>
            <p style={{ fontSize: 14, color: "rgba(255,255,255,.7)", lineHeight: 1.6, margin: 0 }}>
              Para quienes quieren transformación real con el máximo nivel de acompañamiento. Un psicólogo dedicado exclusivamente a ti.
            </p>
            <ul className="pricing-check-list" style={{ color: "rgba(255,255,255,.9)" }}>
              {PLAN_MAX.map((f) => (
                <li key={f}><Check color="#D4B464" />{f}</li>
              ))}
            </ul>
            {renderPlanButton("max", "Max", { background: "#D4B464", color: "#1A1208" })}
          </div>
        </Reveal>
      </div>

      <p style={{ textAlign: "center", fontSize: 13, color: "#8A8A9A", marginTop: 30 }}>
        Los precios incluyen IVA. Cancela cuando quieras. Sin contratos ni permanencia.
      </p>

      {proximamentePlan && (
        <ProximamenteModal planLabel={proximamentePlan} onClose={() => setProximamentePlan(null)} />
      )}
    </div>
  );
}
